import React, { useState, useCallback, useRef } from 'react';
import {
  CODE_LABEL_WIDTH
} from 'constants/DimensionsConstants'
import CodeLabelBody from './CodeLabelBody'
import CodeLabelRight from './CodeLabelRight'
import CodeLabelStateConstants from 'constants/CodeLabelStateConstants'
import { useMergeInteraction } from 'hooks/useMergeInteraction'
import { useCodePageModal } from './contexts/CodePageModalContext'

function CodeLabel({
  id,
  name,
  color,
  onShowMerge, // This will notify parent about height changes
  onCollapseClicked,
  collapsed,
  onRename,
  renameCode,
  deleteCode,
  disabled,
  ghost,
  selected,
  confirmMerge, // From parent (CodeNestedDisplay)
  cancelMerge,  // From parent (CodeNestedDisplay)
  isSearching,
  newCode,
  count,
  renameMode,
  dragOver,
  isDragging,
  onClick,
  allowDropdown,
  initiateMergeCode, // From parent
  canMergeHere,
  transitionType,
  maxDepth,
  setConfirmDrag // From parent (CodeNestedDisplay)
}) {
  const [editableName, setEditableName] = useState("");
  const [hover, setHover] = useState(false);
  
  const codeLabelRef = useRef(null);

  // Merge interaction state and handlers
  const {
    mergeHover,
    showConfirmMerge,
    isInMergeMode,
    handleConfirmMerge,
    handleCancelMerge,
    getMergeHeight,
    mergedName,
    dropRef
  } = useMergeInteraction({
    id,
    name,
    onShowMerge,
    initiateMergeCode,
    confirmMerge,
    cancelMerge,
    setConfirmDrag
  });


  const onCollapseClickedHandler = useCallback((e) => {
    e.preventDefault();
    if (e.stopPropagation) {
      e.stopPropagation();
    } else {
      e.cancelBubble = true;
    }
    onCollapseClicked(id, !!collapsed);
  }, [onCollapseClicked, id, collapsed]);

  const onMouseEnter = () => setHover(true);
  const onMouseLeave = () => setHover(false);

  const { openModal } = useCodePageModal();

  const seeDetails = useCallback(() => {
    if (id) {
      openModal(id);
    }
  }, [id, openModal]);

  // The rAF is load-bearing: mounting the autofocused rename input
  // synchronously inside the menu item's click gets it blurred by the menu's
  // own in-click focus handling, and blur commits-and-closes rename mode
  // (see onBlur below) — so rename mode must be entered after the click
  // settles. The menu's close-time return-to-trigger focus, which used to
  // blur the input from the other side of this race, is suppressed via
  // finalFocus={false} in GlyphDropdown.
  const onRenameHandler = () => {
    window.requestAnimationFrame(() => {
      onRename();
    });
  };

  const onBlur = useCallback(() => {
    renameCode(id, editableName);
  }, [renameCode, id, editableName]);

  const onFocus = useCallback((e) => {
    setEditableName(name);
    e.target.value = '';
    e.target.value = name;
  }, [name]);

  const onChange = (e) => {
    setEditableName(e.target.value);
  };

  const _getCodeName = (newCode, codeName) => {
    return newCode ? "Add Code: " + codeName : codeName;
  };

  const deleteCodeHandler = useCallback(() => {
    if (id)
      deleteCode({
        id,
        name
      });
  }, [deleteCode, id, name]);

  const getCodeState = () => {
    if (isInMergeMode) {
      if (disabled)
        return CodeLabelStateConstants.CODE_MERGING_DISABLED;
      return CodeLabelStateConstants.CODE_MERGING_ENABLED;
    }
    if (ghost)
      return CodeLabelStateConstants.CODE_GHOSTED;
    if (selected)
      return CodeLabelStateConstants.CODE_SELECTED;
    if (disabled)
      return CodeLabelStateConstants.CODE_DISABLED;
    return CodeLabelStateConstants.CODE_ENABLED;
  };

  const isCollapsible = () => {
    return maxDepth > 0 && !ghost && !isSearching;
  };

  const codeName = _getCodeName(newCode, name);
  const showCollapseGlyph = isCollapsible();

  return (
    <div
      ref={codeLabelRef}
      id={id}
      style={{
        height: getMergeHeight(),
        width: (CODE_LABEL_WIDTH + 45)
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <CodeLabelBody
        transitionType={transitionType}
        name={mergedName || codeName}
        color={color}
        count={count}
        edit={renameMode}
        mergeParent={mergeHover}
        dragOver={dragOver}
        isDragging={isDragging}
        showConfirmMerge={showConfirmMerge}
        showCollapseGlyph={showCollapseGlyph}
        collapsed={collapsed}
        onClick={() => onClick({
          id,
          name
        }, selected)}
        onCollapseClicked={onCollapseClickedHandler}
        onBlur={onBlur}
        onFocus={onFocus}
        onChange={onChange}
        editableName={editableName}
        ghost={ghost}
        state={getCodeState()}
        allowPopover={allowDropdown}
        id={id}
        isInMergeMode={isInMergeMode}
      />

      <div style={{ float: "right" }}>
        <CodeLabelRight
          showMerge={canMergeHere || mergeHover || showConfirmMerge} // Show merge zone when dragging over
          mergeHover={mergeHover}
          showConfirmMerge={showConfirmMerge}
          confirmMerge={handleConfirmMerge}
          cancelMerge={handleCancelMerge}
          onRename={onRenameHandler}
          onSeeDetails={seeDetails}
          deleteCode={deleteCodeHandler}
          showDropdown={hover &&
            !newCode &&
            !ghost &&
            allowDropdown
          }
          codeId={id || null}
          codeName={name}
          codeNesterAnchorRef={codeLabelRef.current}
          maxDepth={maxDepth}
          dropRef={dropRef}
        />
      </div>
    </div>
  );
}

export default CodeLabel;